'use client';
import { useAriaLabels } from '@/hooks/useAriaLabels';

import { CMSSection } from '@/components/cms/CmsSection';
import type { ReactNode } from 'react';
import styles from './NewsHeader.module.scss';

interface BreadcrumbItem {
  label: string;
  href: string;
}

interface TagItem {
  value?: string | null;
}

interface NewsHeaderProps {
  /** Breadcrumb configuration */
  breadcrumb?: {
    items?: BreadcrumbItem[];
    inverted?: boolean;
  };
  /** Page title */
  pageTitle?: string;
  /** Published date (ISO string or formatted) */
  publishDate?: string;
  /** Short description text */
  shortDescription?: string;
  /** Tags collection */
  tagsCollection?: {
    items: Array<TagItem | null>;
  };
  /** CMS index for unique data-testid generation */
  cmsIndex?: number;
  /** Optional className */
  className?: string;
  /** Optional children for custom content */
  children?: ReactNode;
}

/**
 * Formats a date string to a readable format
 */
function formatPublishDate(dateString: string): string {
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return dateString;
  }
}

/**
 * NewsHeader - Article/news page header
 *
 * Displays a header section for news/article pages with:
 * - Optional breadcrumb navigation
 * - Category tags
 * - Page title
 * - Publish date
 * - Short description
 *
 * @example
 * <NewsHeader
 *   pageTitle="New Watch Collection Announced"
 *   publishDate="2024-01-15T10:00:00Z"
 *   shortDescription="Discover our latest timepieces..."
 *   tagsCollection={{ items: [{ value: 'News' }, { value: 'Collection' }] }}
 *   cmsIndex={0}
 * />
 */
export function NewsHeader({
  breadcrumb,
  pageTitle,
  publishDate,
  shortDescription,
  tagsCollection,
  cmsIndex,
  className,
}: NewsHeaderProps) {
  const aria = useAriaLabels();
  const testId = (element?: string) =>
    cmsIndex !== undefined
      ? element
        ? `newsheader-${cmsIndex}-${element}`
        : `newsheader-${cmsIndex}`
      : undefined;

  const tags = tagsCollection?.items?.filter((tag): tag is TagItem => !!tag?.value) || [];

  return (
    <>
      {/* Breadcrumb section */}
      {breadcrumb?.items && breadcrumb.items.length > 0 && (
        <CMSSection withMargin={false} data-testid={testId('breadcrumb-section')}>
          <nav aria-label={aria.action('breadcrumb')} className={styles.breadcrumbWrapper}>
            <ol className={styles.breadcrumbList}>
              {breadcrumb.items.map((item, index) => (
                <li key={`${item.href}-${index}`} className={styles.breadcrumbItem}>
                  {index < breadcrumb.items!.length - 1 ? (
                    <>
                      <a href={item.href} className={styles.breadcrumbLink}>
                        {item.label}
                      </a>
                      <span className={styles.breadcrumbSeparator} aria-hidden="true">
                        /
                      </span>
                    </>
                  ) : (
                    <span aria-current="page">{item.label}</span>
                  )}
                </li>
              ))}
            </ol>
          </nav>
        </CMSSection>
      )}

      {/* Main header content */}
      <div className={`${styles.container} ${className || ''}`} data-testid={testId()}>
        <div className={styles.topArea} data-testid={testId('top')}>
          {/* Tags */}
          {tags.length > 0 && (
            <div className={styles.tagWrapper} data-testid={testId('tags')}>
              {tags.map((tag, index) => (
                <span
                  key={`${tag.value}-${index}`}
                  className={styles.tag}
                  data-testid={testId(`tag-${index}`)}
                >
                  {tag.value}
                </span>
              ))}
            </div>
          )}

          {/* Title */}
          {pageTitle && (
            <div className={styles.contentWrapper}>
              <h1 className={styles.title} data-testid={testId('title')}>
                {pageTitle}
              </h1>
            </div>
          )}

          {/* Publish date */}
          {publishDate && (
            <time dateTime={publishDate} className={styles.date} data-testid={testId('date')}>
              {formatPublishDate(publishDate)}
            </time>
          )}
        </div>

        {/* Description */}
        {shortDescription && (
          <div className={styles.contentWrapper}>
            <p className={styles.description} data-testid={testId('description')}>
              {shortDescription}
            </p>
          </div>
        )}
      </div>
    </>
  );
}

NewsHeader.displayName = 'NewsHeader';
